<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { useAccountingStore } from '@/stores/accounting.store'
import { useNotify } from '@/composables/notify'
import { useDetectDevice } from '@/composables/device'

import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'

const props = defineProps({
  manageMode: {
    type: Boolean,
    default: false,
  },
  termType: {
    type: Number,
    default: null,
  },
  label: {
    type: String,
    default: '',
  },
  templateLabel: {
    type: String,
    default: '',
  },
  templateTitle: {
    type: String,
    default: '',
  },
  templates: {
    type: Array,
    default: () => [],
  },
  modelValue: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['refresh', 'update:modelValue', 'update:templateTitle'])

const i18n = getI18n()
const accountingStore = useAccountingStore()
const { notifySuccess } = useNotify()
const { isMobile } = useDetectDevice()

const termTypes = accountingStore.documentTermTemplateTypeEnum

const termTypeOptions = computed(() => [
  { value: termTypes.PAYMENT_TERMS, label: i18n.t('common.paymentTerms') },
  { value: termTypes.PAYMENT_COORDINATES, label: i18n.t('common.paymentCoordinates') },
  { value: termTypes.BUDGET_ESTIMATE_TERMS, label: i18n.t('common.budgetEstimateTerms') },
  { value: termTypes.INTELLECTUAL_PROPERTY_TERMS, label: i18n.t('common.intellectualPropertyTerms') },
])

const fetchByType = {
  [termTypes.PAYMENT_TERMS]: accountingStore.fetchPaymentTermTemplatesAsync,
  [termTypes.PAYMENT_COORDINATES]: accountingStore.fetchPaymentCoordinatesTemplatesAsync,
  [termTypes.BUDGET_ESTIMATE_TERMS]: accountingStore.fetchBudgetEstimateTermTemplatesAsync,
  [termTypes.INTELLECTUAL_PROPERTY_TERMS]: accountingStore.fetchIntellectualPropertyTermTemplatesAsync,
}
const collectionByType = {
  [termTypes.PAYMENT_TERMS]: () => accountingStore.paymentTermTemplates,
  [termTypes.PAYMENT_COORDINATES]: () => accountingStore.paymentCoordinatesTemplates,
  [termTypes.BUDGET_ESTIMATE_TERMS]: () => accountingStore.budgetEstimateTermTemplates,
  [termTypes.INTELLECTUAL_PROPERTY_TERMS]: () => accountingStore.intellectualPropertyTermTemplates,
}

const internalTermType = ref(props.termType ?? termTypes.PAYMENT_TERMS)
const activeTermType = computed(() => props.manageMode ? internalTermType.value : props.termType)
const activeTemplates = computed(() =>
  props.manageMode ? (collectionByType[internalTermType.value]?.() ?? []) : props.templates)

const title = ref(props.templateTitle ?? '')
const descRef = ref(props.modelValue ?? '')
const titleOptions = ref([])

const setTitle = (val) => {
  title.value = val ?? ''
  emit('update:templateTitle', title.value)
}

const description = computed({
  get: () => descRef.value,
  set: (val) => {
    descRef.value = val
    if (!props.manageMode) emit('update:modelValue', val ?? '')
  },
})

const matchingTemplate = computed(() => {
  const needle = title.value.trim().toLowerCase()
  if (!needle) return null
  return activeTemplates.value.find(t => (t.title || '').trim().toLowerCase() === needle) || null
})

const isDirty = computed(() => matchingTemplate.value
  ? descRef.value !== (matchingTemplate.value.description || '')
  : (!!title.value.trim() && !!descRef.value.trim()))

const canSave = computed(() => !!title.value.trim() && !!descRef.value.trim())
const saveLabel = computed(() => matchingTemplate.value ? i18n.t('common.update') : i18n.t('common.addTemplate'))

const descriptionLabel = computed(() => props.label || i18n.t('common.description'))
const titleFieldLabel = computed(() => props.templateLabel || i18n.t('common.title'))

const rootClass = computed(() => {
  if (isMobile.value) return 'q-mb-sm'
  return props.manageMode ? 'row q-col-gutter-md items-start' : 'row q-col-gutter-md items-start q-mb-sm'
})
const titleClass = computed(() => {
  if (isMobile.value) return 'q-mb-sm'
  return props.manageMode ? 'col-3' : 'col-4'
})
const fieldsClass = computed(() => {
  if (isMobile.value) return 'q-mt-sm'
  return props.manageMode ? 'col-6' : 'col-8'
})

const setTitleOptions = () => { titleOptions.value = activeTemplates.value.map(t => t.title) }

const resetForm = () => { setTitle(''); description.value = '' }

const onTypeChange = () => { resetForm(); setTitleOptions() }

const onFilterTitles = (val, update) => {
  update(() => {
    const titles = activeTemplates.value.map(t => t.title)
    titleOptions.value = val ? titles.filter(t => t.toLowerCase().includes(val.toLowerCase())) : titles
  })
}

const onTitleInput = (val) => { setTitle(val) }

const onTitlePicked = (val) => {
  setTitle(val || '')
  if (!val) { description.value = ''; return }
  const template = activeTemplates.value.find(t => t.title === val)
  if (template) description.value = template.description || ''
}

const onSave = async() => {
  const isNew = !matchingTemplate.value
  await accountingStore.upsertDocumentTermTemplateByTitleAsync({
    title: title.value.trim(),
    description: descRef.value,
    term_type: activeTermType.value,
  })
  await fetchByType[activeTermType.value]({}, true)
  setTitleOptions()
  notifySuccess(i18n.t(isNew ? 'info.createdSuccessfully' : 'info.updatedSuccessfully'))
}

const templateToDelete = ref(null)
const showDeleteDialog = computed({
  get: () => templateToDelete.value !== null,
  set: (val) => { if (!val) templateToDelete.value = null },
})

const onDelete = () => { if (matchingTemplate.value) templateToDelete.value = matchingTemplate.value }

const onDeleteConfirmed = async() => {
  await accountingStore.deleteDocumentTermTemplateAsync(templateToDelete.value.id)
  await fetchByType[activeTermType.value]({}, true)
  setTitleOptions()
  resetForm()
  notifySuccess(i18n.t('info.deletedSuccessfully'))
}

// A saved quote whose term came from a template loads only its text; preselect the matching title.
watch(() => props.templates, (list) => {
  if (props.manageMode) return
  if (!title.value && descRef.value && list?.length) {
    const match = list.find(t => t.description === descRef.value)
    if (match) setTitle(match.title)
  }
  setTitleOptions()
}, { immediate: true })

onMounted(async() => {
  if (props.manageMode) {
    await Promise.all(termTypeOptions.value.map(o => fetchByType[o.value]({}, true)))
  }
  setTitleOptions()
})
</script>
<template>
  <div :class="rootClass">
    <q-select
      v-if="manageMode"
      v-model="internalTermType"
      :options="termTypeOptions"
      option-value="value"
      option-label="label"
      emit-value
      map-options
      :label="i18n.t('common.type')"
      :class="isMobile ? 'q-mb-sm' : 'col-3'"
      @update:model-value="onTypeChange"
    />
    <q-select
      v-model="title"
      :options="titleOptions"
      use-input
      fill-input
      hide-selected
      clearable
      new-value-mode="add-unique"
      :label="titleFieldLabel"
      :class="titleClass"
      @filter="onFilterTitles"
      @input-value="onTitleInput"
      @update:model-value="onTitlePicked"
    />
    <div :class="fieldsClass">
      <q-input
        v-model="description"
        type="textarea"
        :label="descriptionLabel"
      />
      <div v-if="manageMode && (isDirty || matchingTemplate)" class="row justify-end q-gutter-sm q-mt-sm">
        <q-btn
          v-if="matchingTemplate"
          color="negative"
          :label="i18n.t('common.delete')"
          @click="onDelete"
        />
        <q-btn
          v-if="isDirty"
          color="positive"
          :label="saveLabel"
          :disable="!canSave"
          @click="onSave"
        />
      </div>
    </div>
    <confirm-dialog
      v-model="showDeleteDialog"
      :message="i18n.t('common.confirmDeleteMessage', { item: templateToDelete?.title })"
      icon="mdi-delete-outline"
      icon-color="negative"
      @confirm="onDeleteConfirmed"
    />
  </div>
</template>
<style lang="scss" scoped>
</style>
